import React from "react";
import { Marker, OverlayView } from "@react-google-maps/api";
import { useSettings } from "../../../context/SettingsContext";
import { useMapIcons } from "../../../hooks/useMapIcons.js";

// ─── Stop Marker ──────────────────────────────────────────────────────────────
// labelOffsetPx: px above the pin-tip to the BOTTOM edge of the label.
// OverlayView anchors its container top-left at the lat/lng point.
// The stop icon has anchor Point(16,40) → pin tip is 40px below icon top,
// so OverlayView is placed AT the pin tip. We offset upward using negative top.
// Layout (all px, measured from pin tip upward):
//   - Icon occupies -40px … 0px  (40px above tip to tip)
//   - Default label bottom = 44px above tip (4px clear of icon top)
//   - connectorH = labelOffsetPx - 40 + 3  (3px overlap into label)
const StopMarker = ({
  stop,
  index,
  stopsLength,
  getOriginIcon,
  getDestinationIcon,
  getIntermediateStopIcon,
  route,
  labelOffsetPx = 44,
}) => {
  const { settings } = useSettings();
  const { getIntermediateStopIcon: fallbackIcon } = useMapIcons(settings, [], [], []);

  // Pick the right icon function based on position in route
  const iconFn =
    index === 0
      ? (getOriginIcon ?? fallbackIcon)
      : index === stopsLength - 1
        ? (getDestinationIcon ?? fallbackIcon)
        : (getIntermediateStopIcon ?? fallbackIcon);

  const STOP_ICON_H = 40;    // icon pixel height (anchor at bottom)
  const LABEL_OVERLAP = 3;   // connector overlaps label bottom by 3 px
  const connectorH = Math.max(0, labelOffsetPx - STOP_ICON_H + LABEL_OVERLAP);

  // OverlayView top-left corner is at pin tip (lat/lng).
  // We shift the container UP by labelOffsetPx so the label sits at the right height.
  // The label itself is absolutely positioned at top: 0 within the shifted container.
  return (
    <React.Fragment key={`stop-${stop.id}-${index}`}>
      <OverlayView
        position={{
          lat: parseFloat(stop.latitude),
          lng: parseFloat(stop.longitude),
        }}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      >
        {/*
          Outer container: anchored at pin tip.
          width/height 0 so it doesn't intercept map events.
        */}
        <div style={{ position: "relative", width: 0, height: 0 }}>
          {/* Label: centred horizontally, bottom edge at labelOffsetPx above pin tip */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: `${labelOffsetPx}px`,
              backgroundColor: "white",
              color: "black",
              padding: "3px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: "500",
              boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
              whiteSpace: "nowrap",
              zIndex: 1001,
              pointerEvents: "none",
            }}
          >
            {stop.name}
          </div>

          {/* Dashed connector line: from icon top up to label bottom */}
          {connectorH > 0 && (
            <svg
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                // bottom of SVG sits at icon top (STOP_ICON_H above pin tip)
                bottom: `${STOP_ICON_H}px`,
                pointerEvents: "none",
                overflow: "visible",
                zIndex: 1000,
              }}
              width="2"
              height={connectorH}
            >
              <line
                x1="1" y1="0" x2="1" y2={connectorH}
                stroke="rgba(0,0,0,0.45)"
                strokeWidth="1.5"
                strokeDasharray="3 2"
              />
            </svg>
          )}
        </div>
      </OverlayView>
      <Marker
        key={`marker-${stop.id}-${index}`}
        position={{
          lat: parseFloat(stop.latitude),
          lng: parseFloat(stop.longitude),
        }}
        icon={iconFn?.() || undefined}
      />
    </React.Fragment>
  );
};

// ─── Vehicle Marker ───────────────────────────────────────────────────────────
// labelOffsetPx: px BELOW the OverlayView anchor to the TOP of the label.
// OverlayView is placed at the vehicle lat/lng (centre of icon).
// The container is shifted down by ANCHOR_SHIFT (13px) matching DashboardMap,
// then the label is placed at top: labelOffsetPx from that shifted anchor.
const VehicleMarker = ({
  position,
  vehicle,
  isMoving,
  heading,
  pathDirection,
  onMarkerClick,
  getVehicleIcon,
  labelOffsetPx = 10,
}) => {
  const { settings } = useSettings();
  const { getVehicleIcon: getCustomVehicleIcon } = useMapIcons(
    settings,
    vehicle ? [vehicle] : [],
    [],
    [],
  );

  if (!position || !isFinite(position.lat) || !isFinite(position.lng)) {
    return null;
  }

  // Prefer the icon function passed in from the parent (has full context: zoom, direction etc.)
  const iconFn = getVehicleIcon ?? getCustomVehicleIcon;
  const vehicleColor = vehicle?.vehicle_color || "#008C23";
  // Connector height: from anchor (top 0 inside shifted container) to label top
  const connectorH = Math.max(0, labelOffsetPx - 2);
  const ANCHOR_SHIFT = 13; // px: shifts container below icon centre (matches DashboardMap)

  return (
    <React.Fragment>
      <OverlayView
        position={position}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      >
        {/* 2×2 anchor div shifted down from icon centre */}
        <div
          style={{
            position: "relative",
            top: `${ANCHOR_SHIFT}px`,
            left: "0px",
            width: "2px",
            height: "2px",
          }}
        >
          {/* Dashed connector from anchor down to label top */}
          {connectorH > 4 && (
            <svg
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                top: "0px",
                pointerEvents: "none",
                overflow: "visible",
                zIndex: 999,
              }}
              width="2"
              height={connectorH}
            >
              <line
                x1="1" y1="0" x2="1" y2={connectorH}
                stroke="black"
                strokeWidth="1.8"
                strokeDasharray="3 2"
              />
            </svg>
          )}
          {/* Vehicle name label */}
          <div
            style={{
              position: "absolute",
              top: `${labelOffsetPx}px`,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: vehicleColor,
              color: "white",
              fontSize: "8px",
              fontWeight: 600,
              fontFamily: "Arial, sans-serif",
              lineHeight: "12px",
              padding: "6px",
              textAlign: "left",
              whiteSpace: "nowrap",
              borderRadius: "10px",
              zIndex: 1000,
              border: `1.5px solid ${vehicleColor}`,
            }}
          >
            {vehicle?.name || "Vehicle"}
          </div>
        </div>
      </OverlayView>
      <Marker
        position={position}
        icon={iconFn(vehicle?.device_id, heading, pathDirection)}
        onClick={onMarkerClick}
      />
    </React.Fragment>
  );
};

const StoppedVehicleIndicator = ({ position, stoppedDuration }) => {
  if (stoppedDuration <= 60 || !position) return null;

  return (
    <Marker
      position={position}
      icon={{
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#FF0000",
        fillOpacity: 0.7,
        strokeWeight: 2,
        strokeColor: "#FFFFFF",
      }}
      title={`Vehicle stopped for ${Math.floor(stoppedDuration / 60)} minutes`}
    />
  );
};

export { StopMarker, VehicleMarker, StoppedVehicleIndicator };
